import React from "react";
import cx from "classnames";
import isNumber from "lodash/isNumber";
import moment from "moment";
import PropTypes from "prop-types";
import Information from "rialto-icons/Information";
import { proposalShape } from "shapes";

import Ner from "components/Ner";
import Price from "components/Price";
import SeeMore from "components/SeeMore";
import SpaceSize from "components/Spaces/components/SpaceSize";
import { Translate } from "utils";

import { costs } from "./Costs";

import styles from "./Proposal.css";

const { t } = new Translate("Proposal");

const discount = (discnt) =>
  discnt < 0
    ? t("negativeDiscount", { percentage: Math.abs(discnt) })
    : t("positiveDiscount", { percentage: Math.abs(discnt) });

export default class ProposalBody extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      isExpanded: props.isExpanded || false,
      isNerOpen: false,
    };

    this.handleToggleExpand = this.handleToggleExpand.bind(this);
    this.handleToggleNer = this.handleToggleNer.bind(this);
  }

  handleToggleExpand() {
    this.setState({
      isExpanded: this.props.isExpandable && !this.state.isExpanded,
    });
  }

  handleToggleNer() {
    this.setState({ isNerOpen: !this.state.isNerOpen });
  }

  render() {
    const { hideDetails, isBordered, isExpandable, proposal } = this.props;
    const { isExpanded, isNerOpen } = this.state;

    if (!proposal) return null;

    return (
      <div
        className={cx(styles.body, {
          [styles.border]: isBordered,
          [styles.showComment]: !hideDetails,
        })}
      >
        <div className={styles.list}>
          {!!proposal.spaces.length && (
            <div className={styles.row}>
              <div className={styles.label}>
                {proposal.spaces.length > 1 ? t("spaces") : t("space")}
              </div>
              <div className={styles.content}>
                <div className={styles.row}>
                  {proposal.spaces.map((space, index) => (
                    <div className={styles.space} key={index}>
                      <div className={styles.name}>{space.name}</div>
                      {space.firstRefusalRight ? (
                        <div className={styles.rofr}>{t("rofr_full")}</div>
                      ) : (
                        <>
                          <Price
                            isBold={false}
                            isPeriodBlack={false}
                            isUnitBlack={false}
                            price={space.price}
                          />
                          <SpaceSize
                            area={space.area}
                            capacity={space.capacity}
                            isUnitBlack={false}
                            isValueBold={false}
                            spaceCategory={space.category}
                          />
                          {space.rentFreePeriod && (
                            <div className={styles.rentFreePeriod}>
                              <span>{space.rentFreePeriod.value}</span>
                              <span>
                                {t(
                                  `units.${space.rentFreePeriod.period}`,
                                  space.rentFreePeriod.value
                                )}
                              </span>
                              <span>{t("free")}</span>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {!hideDetails && !!proposal.ner && (
            <div className={styles.row}>
              <div className={styles.label}>
                {t("ner")}
                <Information onClick={this.handleToggleNer} size="xs" />
              </div>
              <div className={cx(styles.content, styles.ner)}>
                <Price
                  isBold={false}
                  isPeriodBlack={false}
                  price={{ ...proposal.ner, period: "monthly" }}
                />
                {isNumber(proposal.discount) && <small>{discount(proposal.discount)}</small>}
              </div>
              <Ner isOpen={isNerOpen} onClose={this.handleToggleNer} />
            </div>
          )}

          {!!proposal.dueDate && (
            <div className={styles.row}>
              <div className={styles.label}>{t("dueDate")}</div>
              <div className={styles.content}>{moment(proposal.dueDate).format("L")}</div>
            </div>
          )}

          {isExpanded && <br />}

          {!!proposal.term && (
            <div className={styles.row}>
              <div className={styles.label}>{t("term")}</div>
              <div className={styles.content}>
                {proposal.term.value} {t(`units.${proposal.term.period}`, proposal.term.value)}
                {!!proposal.commencementDate && (
                  <>
                    <span className={styles.linkingWord}>{t("from")}</span>
                    {moment(proposal.commencementDate).format("L")}
                  </>
                )}
              </div>
            </div>
          )}

          {isExpanded && (
            <>
              {!!proposal.breakTerm && (
                <div className={styles.row}>
                  <div className={styles.label}>{t("breakTerm")}</div>
                  <div className={styles.content}>
                    <span>{proposal.breakTerm.value}</span>
                    <span>{t(`units.${proposal.breakTerm.period}`, proposal.breakTerm.value)}</span>
                  </div>
                </div>
              )}
              {!!proposal.renewalPeriod && (
                <div className={styles.row}>
                  <div className={styles.label}>{t("renewalPeriod")}</div>
                  <div className={styles.content}>
                    <span>{proposal.renewalPeriod.value}</span>
                    <span>
                      {t(`units.${proposal.renewalPeriod.period}`, proposal.renewalPeriod.value)}
                    </span>
                  </div>
                </div>
              )}

              <br />

              {!!proposal.billingPeriod && (
                <div className={styles.row}>
                  <div className={styles.label}>{t("billingPeriod")}</div>
                  <div className={styles.content}>
                    <span>{proposal.billingPeriod.value}</span>
                    <span>
                      {t(`units.${proposal.billingPeriod.period}`, proposal.billingPeriod.value)}
                    </span>
                    <span className={styles.linkingWord}>{t("with")}</span>
                    <span>{t(`billingMethods.${proposal.billingMethod}`)}</span>
                  </div>
                </div>
              )}
              {!!proposal.deposit && (
                <div className={styles.row}>
                  <div className={styles.label}>{t("deposit")}</div>
                  <div className={styles.content}>
                    <span>{proposal.deposit.value}</span>
                    <span>{t(`units.${proposal.deposit.period}`, proposal.deposit.value)}</span>
                  </div>
                </div>
              )}

              <br />

              {!hideDetails &&
                [...Object.keys(costs), "channelFee", "otherCosts"].map(
                  (cost, index) =>
                    !!proposal[cost] && (
                      <div className={styles.row} key={index}>
                        <div className={styles.label}>
                          {proposal[`${cost}Title`] ? proposal[`${cost}Title`] : t(cost)}
                        </div>
                        <div className={styles.content}>
                          <Price isBold={false} price={{ ...proposal[cost] }} />
                        </div>
                      </div>
                    )
                )}
            </>
          )}
        </div>

        {isExpandable && !isExpanded && (
          <div className={styles.detailsBtn}>
            <SeeMore onClick={this.handleToggleExpand} />
          </div>
        )}
      </div>
    );
  }
}

ProposalBody.propTypes = {
  proposal: PropTypes.shape(proposalShape).isRequired,
  hideDetails: PropTypes.bool,
  isExpandable: PropTypes.bool,
  isExpanded: PropTypes.bool,
  isBordered: PropTypes.bool,
};
